import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';

import { getTextColor } from '@/utils/colorHelpers.js';
import { isDevEnvironment } from '@/utils/helpers.js';

import { useConfigStore, usePrimarySecondaryOverrideStore } from '@/store.js';

const DEFAULT_PRIMARY = '#ffc877';
const DEFAULT_SECONDARY = '#4C726D';

const PRESETS = [
    { label: 'Eterno', primary: DEFAULT_PRIMARY, secondary: DEFAULT_SECONDARY },
    { label: 'Light band', primary: DEFAULT_PRIMARY, secondary: '#CFE3D4' },
    { label: 'Near-white', primary: '#F2E3C0', secondary: '#EDEDED' },
    { label: 'Navy', primary: '#8FB8E8', secondary: '#152A4E' },
    { label: 'Plum', primary: '#E8C6E0', secondary: '#4A1F42' },
];

const useStyles = makeStyles(() => ({
    panel: {
        position: 'fixed',
        left: '1rem',
        top: '0.75rem',
        zIndex: 2147483000,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.75rem',
        background: '#ffffff',
        border: '1px solid #d5d5d5',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        font: '12px/1.4 system-ui, sans-serif',
        color: '#2e2e2e',
    },
    row: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    presets: { display: 'flex', flexWrap: 'wrap', gap: '0.25rem', maxWidth: '16rem' },
    button: {
        padding: '0.25rem 0.5rem',
        border: '1px solid #d5d5d5',
        borderRadius: '0.25rem',
        background: '#fff',
        font: 'inherit',
        cursor: 'pointer',
    },
    swatch: { padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontWeight: 600 },
    toggle: {
        position: 'fixed',
        left: '1rem',
        top: '0.75rem',
        zIndex: 2147483000,
        padding: '0.25rem 0.5rem',
        border: '1px solid #d5d5d5',
        borderRadius: '0.25rem',
        background: '#fff',
        font: '12px system-ui, sans-serif',
        cursor: 'pointer',
    },
}));

const DevPrimarySecondarySelector = () => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const { primaryColor, secondaryColor } = usePrimarySecondaryOverrideStore();
    const currentCustomer = useConfigStore((state) => state.currentCustomer);

    if (!isDevEnvironment()) return null;

    if (!open) {
        return (
            <button type="button" className={classes.toggle} onClick={() => setOpen(true)}>
                🎨 primary / secondary
            </button>
        );
    }

    const primary = primaryColor ?? currentCustomer?.primary_color ?? DEFAULT_PRIMARY;
    const secondary = secondaryColor ?? currentCustomer?.secondary_color ?? DEFAULT_SECONDARY;

    return (
        <div className={classes.panel}>
            <div className={classes.row}>
                <strong style={{ flex: 1 }}>Primary / secondary</strong>
                <button type="button" className={classes.button} onClick={() => setOpen(false)}>
                    ×
                </button>
            </div>

            <div className={classes.presets}>
                {PRESETS.map((preset) => (
                    <button
                        key={preset.label}
                        type="button"
                        className={classes.button}
                        onClick={() =>
                            usePrimarySecondaryOverrideStore.setState({
                                primaryColor: preset.primary,
                                secondaryColor: preset.secondary,
                            })
                        }
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            <div className={classes.row}>
                <span style={{ width: '4.5rem' }}>secondary</span>
                <input
                    aria-label="secondary colour"
                    type="color"
                    value={secondary}
                    onChange={(event) =>
                        usePrimarySecondaryOverrideStore.setState({ secondaryColor: event.target.value })
                    }
                />
                <span className={classes.swatch} style={{ background: secondary, color: getTextColor(secondary) }}>
                    band text
                </span>
            </div>

            <div className={classes.row}>
                <span style={{ width: '4.5rem' }}>primary</span>
                <input
                    aria-label="primary colour"
                    type="color"
                    value={primary}
                    onChange={(event) =>
                        usePrimarySecondaryOverrideStore.setState({ primaryColor: event.target.value })
                    }
                />
                <span className={classes.swatch} style={{ background: primary, color: getTextColor(primary) }}>
                    button text
                </span>
            </div>

            <button
                type="button"
                className={classes.button}
                onClick={() => usePrimarySecondaryOverrideStore.setState({ primaryColor: null, secondaryColor: null })}
            >
                Reset to customer
            </button>
        </div>
    );
};

export default DevPrimarySecondarySelector;
